import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { CSSTransition } from 'react-transition-group';
import Slider from 'react-slick';
import { getNextArrow, getPrevArrow } from './arrows';
import { getFullWidth, throttle, windowAttrs } from '../../../utils/dom';
import { sendAnalyticsEvent } from '../../../utils/analytics';
import {map, sum, noop, uniqueId, capitalize} from 'lodash';

export const CONTAINER_MAX_HORIZONTAL_PX = 1180;
export const DESKTOP_BREAKPOINT = 1220;
export const HANDLE_RESIZE_THROTTLE_MS = 500;
export const NUM_SLIDES_TO_SCROLL = 1;
export const SLICK_CAROUSEL_ACTIVE_LEFT_PADDING = 25;
export const SLICK_CAROUSEL_INITIAL_LEFT_PADDING = 0;
export const SLIDE_TRANSITION_SPEED_MS = 150;
export const SLIDE_TRANSITION_TOUCH_THRESHOLD_DELTA = 30;
export const TRANSITION_ENTER_TIMEOUT_MS = 200;
export const TRANSITION_LEAVE_TIMEOUT_MS = 200;

const defaults = {
  slideType: 'slide',
  slideComponent: noop,
  slideProps: {},
  uuid: uniqueId,
  intl: {}
};

const slickTrackNode = () =>
  document.getElementsByClassName('slick-track')[0];

export default (options = {}) => class extends Component {
  static displayName = capitalize(options.slideType) + 'Carousel';

  static propTypes = {
    activeSlideIdx: PropTypes.number,
    entryId: PropTypes.number,
    handleSlideClick: PropTypes.func,
    seasonIdx: PropTypes.number,
    slides: PropTypes.arrayOf(PropTypes.object).isRequired,
    throttleWait: PropTypes.number.isRequired
  };

  static defaultProps = {
    throttleWait: HANDLE_RESIZE_THROTTLE_MS
  };

  constructor(props, context) {
    super(props, context);
    Object.assign(this, defaults, options);
    this._slideWidths = new Array(this.props.slides.length);
    this.state = { shouldHideNextArrow: true };
    this.handleResize = throttle(
      this.handleResize.bind(this),
      this.props.throttleWait
    );
  }

  componentDidMount() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    const shouldHideNextArrow = !this.carouselWidthExceedsContainerWidth();
    if (shouldHideNextArrow && !this.state.shouldHideNextArrow) {
      this.setState({ shouldHideNextArrow: true });
    } else if (!shouldHideNextArrow && this.state.shouldHideNextArrow) {
      this.setState({ shouldHideNextArrow: false });
    }
  }

  carouselWidth = () =>
    sum(this._slideWidths);

  containerWidth = () =>
    Math.min(
      windowAttrs().innerWidth,
      CONTAINER_MAX_HORIZONTAL_PX
    );

  carouselWidthExceedsContainerWidth = () =>
    this.carouselWidth() > this.containerWidth();

  toSlideComponent = (slide, idx) => (
    <div key={idx}
      className={`${options.slideType}-container`}
      ref={node => this._slideWidths[idx] = getFullWidth(node, window)}
    >
      {
        React.createElement(
          this.slideComponent,
          this.slideProps(slide, idx)
        )
      }
    </div>
  );

  slideComponents = () =>
    map(this.props.slides, this.toSlideComponent)

  sliderProps = (uuid, intl) => ({
    arrows: true,
    dots: false,
    infinite: false,
    slidesToScroll: NUM_SLIDES_TO_SCROLL,
    touchThreshold: SLIDE_TRANSITION_TOUCH_THRESHOLD_DELTA,
    speed: SLIDE_TRANSITION_SPEED_MS,
    variableWidth: true,
    swipe: false,
    swipeToSlide: false,
    touchMove: false,
    draggable: false,
    responsive: [
      {
        breakpoint: DESKTOP_BREAKPOINT,
        settings: {
          swipe: true,
          swipeToSlide: true,
          touchMove: true,
          draggable: true
        }
      }
    ],
    prevArrow: React.createElement(
      getPrevArrow(uuid, 'angle-left', intl)
    ),
    nextArrow: React.createElement(
      getNextArrow(uuid, 'video', { shouldHide: this.state.shouldHideNextArrow }, 'angle-right', intl)
    ),
    afterChange: () => sendAnalyticsEvent(
      `Video Series ${capitalize(options.slideType)} Carousel`,
      'Swipe'
    ),
    beforeChange: (currentSlide, nextSlide) => {
      if (currentSlide === 0) {
        slickTrackNode().style.left =
          `${SLICK_CAROUSEL_ACTIVE_LEFT_PADDING}px`;
      } else if (nextSlide === 0) {
        slickTrackNode().style.left =
          `${SLICK_CAROUSEL_INITIAL_LEFT_PADDING}px`;
      }
    }
  });

  render() {
    const uuid = this.uuid();
    return (
      <CSSTransition
        classNames="fade"
        timeout={{
          enter: TRANSITION_ENTER_TIMEOUT_MS,
          exit: TRANSITION_LEAVE_TIMEOUT_MS
        }}
      >
        <div key={uuid}
          className={`${options.slideType}-carousel`}
          data-season-idx={this.props.seasonIdx}
        >
          <Slider {...this.sliderProps(uuid, this.intl())}>
            {this.slideComponents()}
          </Slider>
        </div>
      </CSSTransition>
    );
  }
};
